/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { mergeRegister } from "@lexical/utils";
import * as React from "react";

import ImagePlugin from "./plugin/image/ImagePlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ImageNode } from "./plugin/image/ImageNode";
import classNames from "classnames";
import { COMMAND_PRIORITY_CRITICAL, EditorState, INSERT_LINE_BREAK_COMMAND, KEY_ENTER_COMMAND, LexicalEditor } from "lexical";
import { QuoteNode } from "./plugin/quote/QuoteNode";
import QuotePlugin from "./plugin/quote/QuotePlugin";
import DocumentPlugin from "./plugin/document/DocumentPlugin";
import { DocumentNode } from "./plugin/document/DocumentNode";

export default function Editor({
    className,
    placeholder,
    onEnterPressed,
    onChange,
    editorRef
}: {
    className?: string;
    placeholder: string;
    onEnterPressed?: (event: KeyboardEvent) => void;
    onChange?: (editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => void;
    editorRef: ((instance: LexicalEditor) => void) | React.MutableRefObject<LexicalEditor>;
}): JSX.Element {
    const initialConfig = {
        namespace: "Symboolic",
        nodes: [ImageNode, QuoteNode, DocumentNode],
        onError: (error: Error) => {
            throw error;
        }
    };

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <div className="editor-shell w-full">
                <div className="editor-container plain-text relative">
                    <AutoFocusPlugin />
                    <ClearEditorPlugin />
                    <PlainTextPlugin
                        contentEditable={
                            <ContentEditable className={classNames(className, "ContentEditable__root overflow-y-auto px-3 py-2 resize-none")} />
                        }
                        placeholder={
                            <div className="absolute top-0 h-full flex px-3 text-blue-lightest items-center pointer-events-none">
                                <span>{placeholder}</span>
                            </div>
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <HistoryPlugin />
                    <ImagePlugin />
                    <DocumentPlugin />
                    <QuotePlugin />
                    <OnChangePlugin onChange={onChange} />
                    <MaxRowPlugin maxRows={5} />
                    <KeyEnterPlugin onEnterPressed={onEnterPressed} />
                    <EditorRefPlugin editorRef={editorRef} />
                </div>
            </div>
        </LexicalComposer>
    );
}

function KeyEnterPlugin({ onEnterPressed }: { onEnterPressed?: (event: KeyboardEvent) => void }): JSX.Element | null {
    const [editor] = useLexicalComposerContext();

    React.useEffect(() => {
        let insert = false;

        return mergeRegister(
            editor.registerCommand(
                INSERT_LINE_BREAK_COMMAND,
                () => {
                    if (insert) {
                        insert = false;
                        return false;
                    }
                    return true;
                },
                COMMAND_PRIORITY_CRITICAL
            ),
            editor.registerCommand(
                KEY_ENTER_COMMAND,
                (event: KeyboardEvent) => {
                    if (event.ctrlKey || event.metaKey) {
                        insert = true;
                    } else {
                        onEnterPressed?.(event);
                    }
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL
            )
        );
    }, [editor, onEnterPressed]);

    return null;
}

function MaxRowPlugin({ maxRows }: { maxRows: number }): JSX.Element | null {
    const [editor] = useLexicalComposerContext();

    React.useEffect(() => {
        const root = editor.getRootElement();
        const lineHeight = getLineHeight(root);
        const gap = root.clientHeight - lineHeight;
        const maxHeight = lineHeight * maxRows + gap;
        root.style.maxHeight = maxHeight.toString() + "px";
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
}

function getLineHeight(el: HTMLElement): number {
    var temp = document.createElement(el.nodeName),
        ret;
    temp.setAttribute(
        "style",
        "margin:0; padding:0; font-family:" + (el.style.fontFamily || "inherit") + "; font-size:" + (el.style.fontSize || "inherit")
    );
    temp.innerHTML = "A";

    el.parentNode.appendChild(temp);
    ret = temp.clientHeight;
    temp.parentNode.removeChild(temp);

    return ret;
}
